import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { MoreMenu, type MoreItem } from "./more-menu";

/**
 * The calm page header: a title, a one-line hint, ONE primary action and a "More" menu.
 *
 * - `primary`: the single filled action (`<Button variant="brand">`). Leave it out when the page has none.
 * - `secondary`: at most two quiet (`outline`/`ghost`) buttons. Prefer `more`.
 * - `more`: everything else, in the overflow menu (danger items last).
 * - `meta`: a small line under the hint (status chip, last synced…); keep it short.
 *
 * In development a warning is logged when `secondary` holds a `brand` button: one primary per screen.
 */
export function PageHeader({
  title,
  hint,
  primary,
  secondary,
  more,
  meta,
  eyebrow,
  className,
  testId,
}: {
  title: ReactNode;
  hint?: ReactNode;
  primary?: ReactNode;
  secondary?: ReactNode;
  more?: MoreItem[];
  meta?: ReactNode;
  eyebrow?: ReactNode;
  className?: string;
  testId?: string;
}) {
  if (import.meta.env.DEV) warnOnSecondBrand(secondary);
  const hasActions = !!(primary || secondary || more?.length);
  return (
    <header className={cn("mb-6 flex flex-wrap items-start gap-x-4 gap-y-3", className)} data-testid={testId}>
      <div className="min-w-[min(100%,18rem)] flex-1">
        {eyebrow && <div className="mb-1 text-[12px] font-medium text-ink-3">{eyebrow}</div>}
        <h1 className="text-[length:var(--text-h1)] leading-tight font-semibold tracking-tight text-ink">{title}</h1>
        {hint && <p className="mt-1.5 max-w-2xl text-[length:var(--text-hint)] text-ink-3">{hint}</p>}
        {meta && <div className="mt-2 flex flex-wrap items-center gap-2 text-[12.5px] text-ink-3">{meta}</div>}
      </div>
      {hasActions && (
        <div className="flex flex-wrap items-center gap-2">
          {more && more.length > 0 && <MoreMenu items={more} testId="page-more" />}
          {secondary}
          {primary}
        </div>
      )}
    </header>
  );
}

function warnOnSecondBrand(node: ReactNode) {
  const list = Array.isArray(node) ? node : [node];
  for (const n of list) {
    if (n && typeof n === "object" && "props" in n && (n.props as { variant?: string })?.variant === "brand") {
      console.warn("[PageHeader] only `primary` may hold a brand button: one primary action per screen.");
      return;
    }
  }
}
